import { getPassScoreLabel } from "@repo/app-types/constants/passScore";
import {
  checkIsSectionFeedbackWithCurrentGrade,
  sectionFeedbackCorrectValueToDisplayValue,
  type FeedbackCallMetadata,
  type FeedbackWithCurrentGrade,
  type RecordingAssessmentFeedbackStatus,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { evaluateFeedbackCorrectness } from "@repo/app-types/helpers/feedbackHelpers";
import { parseGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { DateTime } from "luxon";

/**
 * @file Display logic for the playbook editor's feedback review table. Kept
 * apart from the cells so the branching (which value type, expected grade or
 * not, missing call metadata) is unit-testable without rendering a table.
 */

/** Shown where a grade exists but holds no value we can render. */
const MISSING_GRADE_DISPLAY_VALUE = "--";

const UNKNOWN_CONTACT_NAME = "Unknown contact";
const UNKNOWN_CALL_DATE = "Unknown date";
const UNKNOWN_FEEDBACK_AUTHOR = "a teammate";

/** Empty-state wording that says which list is empty, and for what node. */
export function getFeedbackReviewEmptyMessage(
  status: RecordingAssessmentFeedbackStatus,
  isSectionSelected: boolean,
): string {
  return isSectionSelected
    ? `No ${status} feedback for this section`
    : `No ${status} feedback for the selected criteria`;
}

/**
 * The renderable form of a stored grading value — a section as its
 * applicability, a criterion through the shared grading-value parser — or
 * `undefined` when the value is blank or unparseable for its node's type.
 */
function getRenderableFeedbackValue(
  feedback: FeedbackWithCurrentGrade,
  value: string | undefined,
): string | undefined {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return undefined;
  }

  if (checkIsSectionFeedbackWithCurrentGrade(feedback)) {
    return sectionFeedbackCorrectValueToDisplayValue(trimmedValue);
  }

  const parsed = parseGradingValue(
    feedback.data.criteria_node.grading_value_type,
    trimmedValue,
  );
  if (parsed === undefined) {
    return undefined;
  }
  if (parsed.value === null) {
    return "N/A";
  }

  // A bare "5", not the shared formatter's "5/5" — this table shows two score
  // columns side by side, where the denominator is noise. Rendering from the
  // parsed value keeps a stored "4.6" in the same bare format.
  return parsed.type === "1_5_score"
    ? String(parsed.value)
    : getPassScoreLabel(parsed.value);
}

/** Renders a grading value the way its criteria node grades. */
export function getFeedbackGradeDisplayValue(
  feedback: FeedbackWithCurrentGrade,
  value: string | undefined,
): string {
  return (
    getRenderableFeedbackValue(feedback, value) ?? MISSING_GRADE_DISPLAY_VALUE
  );
}

/**
 * The grade the reviewer said was right, or `undefined` when the submission
 * carries no renderable expected value — those rows are explanation-only.
 */
export function getFeedbackExpectedGradeDisplayValue(
  feedback: FeedbackWithCurrentGrade,
): string | undefined {
  return getRenderableFeedbackValue(feedback, feedback.data.correct_value);
}

/**
 * How the expected grade compares to the grade the reviewer saw — the grade
 * this table shows alongside it, not the live one — so the icon always agrees
 * with the value in the adjacent column. `undefined` when there is nothing to
 * compare against.
 */
export function getFeedbackGradeCorrectness(
  feedback: FeedbackWithCurrentGrade,
): { isCorrect: boolean; isClose: boolean } | undefined {
  if (getFeedbackExpectedGradeDisplayValue(feedback) === undefined) {
    return undefined;
  }

  return evaluateFeedbackCorrectness(feedback, feedback.data.grading.value);
}

interface FeedbackCallDisplayLines {
  /** Contact name, falling back to the secondary line and then a placeholder. */
  primaryName: string;
  /** Phone/email line, omitted when it would repeat {@link primaryName}. */
  secondaryName: string | undefined;
  recordedAtLabel: string;
}

/** The stacked lines identifying the call a feedback row came from. */
export function getFeedbackCallDisplayLines(
  call: FeedbackCallMetadata,
): FeedbackCallDisplayLines {
  const displayName = call.call_display_name?.trim();
  const secondaryDisplayName = call.call_secondary_display_name?.trim();
  const primaryName =
    displayName || secondaryDisplayName || UNKNOWN_CONTACT_NAME;

  return {
    primaryName,
    secondaryName:
      secondaryDisplayName && secondaryDisplayName !== primaryName
        ? secondaryDisplayName
        : undefined,
    recordedAtLabel: formatFeedbackCallDate(call.call_recorded_at),
  };
}

/** The call's recording date in the viewer's timezone. */
function formatFeedbackCallDate(recordedAt: string | null): string {
  if (!recordedAt) {
    return UNKNOWN_CALL_DATE;
  }

  const recordedAtDateTime = DateTime.fromISO(recordedAt);
  return recordedAtDateTime.isValid
    ? recordedAtDateTime.toLocaleString(DateTime.DATE_MED)
    : UNKNOWN_CALL_DATE;
}

/** Who submitted the feedback and when, in the viewer's timezone. */
export function getFeedbackAttributionText(
  feedback: FeedbackWithCurrentGrade,
): string {
  const authorName =
    feedback.feedback_author_name?.trim() || UNKNOWN_FEEDBACK_AUTHOR;
  const submittedAt = DateTime.fromISO(feedback.created_at);

  return submittedAt.isValid
    ? `Submitted by ${authorName} on ${submittedAt.toLocaleString(DateTime.DATETIME_MED)}`
    : `Submitted by ${authorName}`;
}

/** Why the feedback is in its current lifecycle state, when it was automated or reopened. */
export function getFeedbackCurrentStateReason(
  feedback: FeedbackWithCurrentGrade,
): string | undefined {
  // A row may be born resolved when its submitted expected value already
  // matches the grade. It still counts as newly submitted: lifecycle reasons
  // replace attribution only after the row has actually been updated.
  if (feedback.updated_at === feedback.created_at) {
    return undefined;
  }

  const currentStatusChange = feedback.data.status_changes
    ?.filter((change) => change.status === feedback.status)
    .toSorted(
      (left, right) =>
        DateTime.fromISO(left.changed_at).toMillis() -
        DateTime.fromISO(right.changed_at).toMillis(),
    )
    .at(-1);
  if (!currentStatusChange) {
    return undefined;
  }

  const changedAt = DateTime.fromISO(currentStatusChange.changed_at);
  if (!changedAt.isValid) {
    return undefined;
  }
  const changedAtLabel = changedAt.toLocaleString(DateTime.DATETIME_MED);

  if (
    currentStatusChange.reason === "manual" ||
    currentStatusChange.reason === "feedback_update"
  ) {
    const authorName =
      currentStatusChange.author_name?.trim() || UNKNOWN_FEEDBACK_AUTHOR;
    switch (feedback.status) {
      case "open":
        return `Reopened by ${authorName} on ${changedAtLabel}`;
      case "resolved":
        return `Resolved by ${authorName} on ${changedAtLabel}`;
      case "outdated":
        return `Marked outdated by ${authorName} on ${changedAtLabel}`;
    }
  }

  if (
    feedback.status === "outdated" &&
    currentStatusChange.reason === "playbook_change"
  ) {
    const nodeType = checkIsSectionFeedbackWithCurrentGrade(feedback)
      ? "section"
      : "criteria";
    return `Outdated due to ${nodeType} change on ${changedAtLabel}`;
  }

  if (
    feedback.status === "resolved" &&
    currentStatusChange.reason === "matched_grading"
  ) {
    return `Resolved due to regrade on ${changedAtLabel}`;
  }

  if (
    feedback.status === "outdated" &&
    currentStatusChange.reason === "evidence_changed"
  ) {
    return `Outdated due to call change on ${changedAtLabel}`;
  }

  return undefined;
}
